import type { components, operations } from '@molchanovs/api-contract';
import { z } from 'zod';

/** Default JSON representation for GET /api/v1/user-profiles/{profileRef}. */
export type CrmCustomerProfileResponse = components['schemas']['CrmUserProfileDetailResponse'];

export type CrmCustomerProfile = CrmCustomerProfileResponse['data'];
export type CrmCustomerProfileFieldVisibility = NonNullable<
  CrmCustomerProfile['sensitiveFieldVisibility']
>[keyof NonNullable<CrmCustomerProfile['sensitiveFieldVisibility']>];

export type CrmCustomerProfileMutationStateResponse =
  components['schemas']['CrmUserProfileMutationStateResponse'];
export type CrmCustomerProfilePatch =
  operations['patchApiV1UserProfilesByProfileRef']['requestBody']['content']['application/merge-patch+json'];
export type CrmCustomerProfileSexesResponse =
  operations['getApiV1UserProfileSexes']['responses'][200]['content']['application/json'];
export type CrmCustomerProfileStatusesResponse =
  operations['getApiV1UserProfileStatuses']['responses'][200]['content']['application/json'];
export type CrmCustomerProfileMainLocationsResponse =
  components['schemas']['CrmUserProfileMainLocationCatalogResponse'];
export type CrmCustomerProfileBusinessLinesResponse =
  operations['getApiV1BusinessLines']['responses'][200]['content']['application/json'];

const nullableUuid = z.uuid().nullable();

export const crmCustomerProfileMutationStateSchema: z.ZodType<CrmCustomerProfileMutationStateResponse> =
  z.object({
    data: z.object({
      id: z.uuid(),
      profile: z.object({
        addressCity: z.string().nullable(),
        addressCountry: z.string().nullable(),
        allLanguages: z.array(z.uuid()),
        birthDate: z.iso.date().nullable(),
        businessLineKeys: z.array(z.string().regex(/^[a-z][a-z0-9_]{1,63}$/u)).optional(),
        city: z.string().nullable(),
        country: z.string().nullable(),
        firstName: z.string().nullable(),
        highestDiscount: z.number().nullable(),
        instructorSuspended: z.boolean().nullable(),
        isJunior: z.boolean().nullable(),
        lastName: z.string().nullable(),
        mainLocationRef: nullableUuid,
        middleName: z.string().nullable(),
        paymentsFromStudentsAllowed: z.boolean().nullable(),
        preferredLanguage: nullableUuid,
        sex: nullableUuid,
        userStatus: nullableUuid,
        volumeDiscount: z.number().nullable(),
      }),
    }),
    meta: z.object({ mutationRevision: z.string() }),
  });

const profileOptionSchema = z.object({ id: z.uuid(), label: z.string() });

export const crmCustomerProfileSexesSchema: z.ZodType<CrmCustomerProfileSexesResponse> = z.object({
  data: z.object({ sexes: z.array(profileOptionSchema) }),
});

export const crmCustomerProfileStatusesSchema: z.ZodType<CrmCustomerProfileStatusesResponse> =
  z.object({
    data: z.object({ userStatuses: z.array(profileOptionSchema) }),
  });

export const crmCustomerProfileMainLocationsSchema: z.ZodType<CrmCustomerProfileMainLocationsResponse> =
  z.object({
    data: z.object({
      hasMore: z.boolean(),
      mainLocations: z.array(profileOptionSchema),
    }),
  });

export const crmCustomerProfileBusinessLinesSchema: z.ZodType<CrmCustomerProfileBusinessLinesResponse> =
  z.object({
    data: z.object({
      businessLines: z
        .array(
          z.object({
            id: z.uuid(),
            key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/u),
            displayName: z.string().min(1),
            description: z.string().nullable(),
            sortOrder: z.number().int().nonnegative(),
            status: z.enum(['active', 'suspended']),
          })
        )
        .max(200),
    }),
  });
